/**
 * Legacy function: Converts time ranges to individual 30-minute slots (without employee data)
 * @param {Array<Object>} ranges - Array of objects with time_range property
 * @returns {Array<string>} Array of time slots in 'HH:MM' format
 * @deprecated Use convertRangesToSlotsWithEmployees for new implementations
 */
// eslint-disable-next-line no-unused-vars
function convertRangesToIndividualSlots(ranges) {
    const slots = new Set(); // Use Set to avoid duplicates
    
    ranges.forEach(range => {
        const [startTime, endTime] = range.split('-');
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        
        // Convert to minutes for easier calculation
        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;
        
        // Generate 30-minute slots
        for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
            const hour = Math.floor(minutes / 60);
            const minute = minutes % 60;
            const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            slots.add(timeSlot);
        }
    });
    
    
    // Convert Set to Array and sort
    return Array.from(slots).sort();
}

/**
 * Converts time ranges with employee data to individual time slots with available employees
 * @param {Array<Object>} availableSlots - Array of objects with time_range and available_employees
 * @returns {Array<Object>} Array of objects with timeSlot and availableEmployees (30-minute intervals)
 */
function convertRangesToSlotsWithEmployees(availableSlots) {
    const slotsMap = new Map(); // Use Map to handle overlapping time slots and merge employees
    
    availableSlots.forEach(slot => {
        const [startTime, endTime] = slot.time_range.split('-');
        const [startHour, startMinute] = startTime.split(':').map(Number);
        const [endHour, endMinute] = endTime.split(':').map(Number);
        
        // Convert to minutes for easier calculation
        const startMinutes = startHour * 60 + startMinute;
        const endMinutes = endHour * 60 + endMinute;
        
        // Generate 30-minute slots
        for (let minutes = startMinutes; minutes < endMinutes; minutes += 30) {
            const hour = Math.floor(minutes / 60);
            const minute = minutes % 60;
            const timeSlot = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
            
            // If slot already exists, merge employees; otherwise create new entry
            if (slotsMap.has(timeSlot)) {
                const existingEmployees = slotsMap.get(timeSlot);
                const mergedEmployees = [...new Set([...existingEmployees, ...slot.available_employees])];
                slotsMap.set(timeSlot, mergedEmployees);
            } else {
                slotsMap.set(timeSlot, [...slot.available_employees]);
            }
        }
    });
    
    // Convert Map to array of objects and sort by time
    const result = Array.from(slotsMap.entries()).map(([timeSlot, availableEmployees]) => ({
        timeSlot,
        availableEmployees
    }));
    
    return result.sort((a, b) => a.timeSlot.localeCompare(b.timeSlot));
}

/**
 * Fetches available time slots for a specific date (legacy function for backward compatibility)
 * @param {Date} date - The date to check availability for
 * @returns {Promise<Array<string>>} Array of individual time slots in HH:MM format
 */
export async function fetchAvailableTimeSlots(date) {
    try {
        const functionUrl = import.meta.env.VITE_FIREBASE_FUNCTION_URL_SLOTS;
        if (!functionUrl) {
            throw new Error('Firebase function URL not configured');
        }

        const formattedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];

        console.log('formattedDate:', formattedDate);
        
        // Use GET request with day as query parameter (legacy format)
        const response = await fetch(`${functionUrl}?day=${formattedDate}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch available slots: ${response.status}`);
        }

        const data = await response.json();
        console.log('Available slots raw data:', data.rawData);
        
        // For backward compatibility, return the legacy format
        const individualSlots = data.availableSlots || [];
        
        console.log('Individual time slots:', individualSlots);
        return individualSlots;

    } catch (error) {
        console.error('Error fetching available slots:', error);
        throw error;
    }
}

/**
 * Fetches available time slots with employee data for a specific date
 * @param {Date} date - The date to check availability for
 * @returns {Promise<Array<Object>>} Array of objects with timeSlot and availableEmployees
 */
export async function fetchAvailableSlotsWithEmployees(date) {
    try {
        const functionUrl = import.meta.env.VITE_FIREBASE_FUNCTION_URL_SLOTS;
        if (!functionUrl) {
            throw new Error('Firebase function URL not configured');
        }

        const formattedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];

        console.log('formattedDate:', formattedDate);
        
        // Use GET request with includeEmployees parameter to get new format
        const response = await fetch(`${functionUrl}?day=${formattedDate}&includeEmployees=true`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch available slots with employees: ${response.status}`);
        }

        const data = await response.json();
        console.log('Available slots with employees raw data:', data.rawData);
        console.log('Processed slots with employees:', data.availableSlots);
        
        // Return the structured data with time slots and employee information
        return data.availableSlots || [];

    } catch (error) {
        console.error('Error fetching available slots with employees:', error);
        throw error;
    }
}

/**
 * Alternative function that processes raw data on the frontend (for flexibility)
 * @param {Date} date - The date to check availability for
 * @returns {Promise<Array<Object>>} Array of objects with timeSlot and availableEmployees
 */
export async function fetchAndProcessSlotsWithEmployees(date) {
    try {
        const functionUrl = import.meta.env.VITE_FIREBASE_FUNCTION_URL_SLOTS;
        if (!functionUrl) {
            throw new Error('Firebase function URL not configured');
        }

        const formattedDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .split('T')[0];

        console.log('formattedDate:', formattedDate);
        
        // Get raw data and process on frontend
        const response = await fetch(`${functionUrl}?day=${formattedDate}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch available slots: ${response.status}`);
        }

        const data = await response.json();
        console.log('Available slots raw data:', data.rawData);
        
        // Process raw data on frontend using the new function
        const slotsWithEmployees = convertRangesToSlotsWithEmployees(data.rawData || []);
        
        console.log('Processed slots with employees:', slotsWithEmployees);
        return slotsWithEmployees;

    } catch (error) {
        console.error('Error fetching and processing slots with employees:', error);
        throw error;
    }
}
